import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Tab,
  Tabs,
  Typography,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SettingsIcon from '@mui/icons-material/Settings';
import DotCarousel from '../components/DotCarousel';
import ProjectFiltersDialog from '../components/ProjectFiltersDialog';
import MonthRangePicker, { lastNMonthsRange, formatMonthRange, type MonthRange } from '../components/MonthRangePicker';
import ProjectNavButtons from '../components/ProjectNavButtons';
import PageSearchBar from '../components/PageSearchBar';
import { PosterCard, type PosterItem } from '../components/PosterCard';
import { PaginatedPosterGrid } from '../components/PaginatedPosterGrid';
import { logEdmPreviewed, logEdmDownloaded } from '../services/analytics';
import {
  projectEssentialApi,
  type EdmTabId,
  type EdmTabItem,
  type ProjectFilters,
} from '../services/api';
import { useAppliedFilterChips } from '../hooks/useAppliedFilterChips';
import { usePeFilters } from '../hooks/usePeFilters';
import { usePeSearchDropdown } from '../hooks/usePeSearchDropdown';
import SearchPendingPreviewHost from '../components/SearchPendingPreviewHost';
import {
  buildPeSearchPageHref,
  countPeFilters,
} from '../utils/searchState';

// ---------------------------------------------------------------------------
// EDM page card adapter. The shared `PosterItem` shape is re-used as-is
// so the catalogue grid and the "Uploaded Recently" carousel render
// identically to the listing pages. We just need a small adapter from
// the API's `EdmTabItem` to that shape — everything else is delegated.
// ---------------------------------------------------------------------------

// Format the EDM `createdAt` ISO into the "08 May 2026" style the cards
// expect. Returns an empty string when the timestamp is missing so the
// card just hides the date row instead of printing "Invalid Date".
function formatEdmDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function mapTabItem(row: EdmTabItem): PosterItem {
  return {
    id: row.id,
    title: row.title,
    date: formatEdmDate(row.createdAt),
    cover: row.cover,
    downloads: row.downloads,
    url: row.url,
    urlPublic: row.urlPublic,
    filename: row.filename,
  };
}

const MAX_UPLOADED_RECENTLY = 10;

// Smaller card sizing than the Highlights page so multiple posters are
// visible side-by-side, matching the EDM mock. Used by the
// “Uploaded Recently” carousel at the top of the page.
const EDM_SLIDE_FLEX = {
  xs: '0 0 52%',
  sm: '0 0 36%',
  md: '0 0 26%',
  lg: '0 0 20%',
};

// ---------------------------------------------------------------------------
// Catalogue (chip-filtered grid below “Uploaded Recently”)
//
// We expose the entire EDM library as a single, paginated grid with a
// chip row that lets the user narrow by category. This replaces the old
// nested pattern of accordions-of-carousels which made the inventory
// hard to scan and which created label collisions with the page-level
// Pre Filter buttons (e.g. “Seminars & Events”). The chip labels are
// EDM-specific so the user is never in doubt that they’re still on the
// EDM page.
// ---------------------------------------------------------------------------

const EDM_CATEGORIES = [
  { id: 'all', label: 'All' },
  { id: 'upcoming', label: 'Upcoming Projects' },
  { id: 'projects', label: 'Projects' },
  { id: 'seminars', label: 'Seminar & Events' },
  { id: 'property-guides', label: 'Property Guides' },
  { id: 'vvip', label: 'VVIP Passes' },
] as const;

type EdmCategoryId = typeof EDM_CATEGORIES[number]['id'] | 'hidden';
type ModifiedEdmView = 'hidden' | 'ec';

// ---------------------------------------------------------------------------
// Building blocks
// ---------------------------------------------------------------------------

// Same dashboard-style heading used on the Highlights & eBook page.
// Plain — the EDM page no longer collapses individual sections (the
// catalogue replaces the per-category accordions), so this is a simple
// uppercase title row with no toggle affordance.
function SectionHeader({ title }: { title: string }) {
  return (
    <Box sx={{ mb: 1.25 }}>
      <Typography
        variant="subtitle1"
        sx={{
          fontWeight: 700,
          color: 'var(--color-text-strong)',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          fontSize: { xs: '0.95rem', sm: '1.05rem' },
        }}
      >
        {title}
      </Typography>
    </Box>
  );
}

type EdmTabCacheKey = `${EdmCategoryId}|${string}`;

function projectFiltersCacheKey(filters: ProjectFilters): string {
  return [
    filters.regions?.join(',') ?? '',
    filters.districts?.join(',') ?? '',
    filters.propertyTypes?.join(',') ?? '',
    filters.projectIds?.join(',') ?? '',
    filters.soleMarketing ? '1' : '0',
  ].join(';');
}

function edmTabCacheKey(
  category: EdmCategoryId,
  search: string,
  filters: ProjectFilters,
  monthRange: MonthRange | null,
  modifiedView: ModifiedEdmView,
): EdmTabCacheKey {
  // The month range is part of the cache key so that swapping months
  // re-fetches instead of replaying the previous month's cached page.
  // When search is active the date window is dropped (`all-dates`).
  const monthKey = monthRange ? `${monthRange.start}~${monthRange.end}` : 'all-dates';
  const viewKey = category === 'hidden' ? modifiedView : 'catalogue';
  return `${category}|${search}|${projectFiltersCacheKey(filters)}|${monthKey}|${viewKey}`;
}

// Catalogue grid: tab-filtered, paginated grid of every EDM in the
// library. Replaces the old accordion-per-category pattern so users can
// scan the whole inventory at a glance and narrow with a single tap.
function EdmCatalogue({
  searchQuery,
  filters,
  monthRange,
  onMonthRangeChange,
  isAdmin = false,
}: {
  searchQuery: string;
  filters: ProjectFilters;
  monthRange: MonthRange;
  onMonthRangeChange: (next: MonthRange) => void;
  isAdmin?: boolean;
}) {
  const [category, setCategory] = useState<EdmCategoryId>('all');
  // Lazy per-(tab, search, filters) cache so switching tabs doesn't
  // re-hit the network, but a new search or filter set refetches.
  const [tabData, setTabData] = useState<Partial<Record<EdmTabCacheKey, PosterItem[]>>>({});
  const [loadingTabs, setLoadingTabs] = useState<Partial<Record<EdmTabCacheKey, boolean>>>({});
  const [serverTabCounts, setServerTabCounts] = useState<Partial<Record<EdmTabId, number>>>({});
  const [error, setError] = useState<string | null>(null);
  // Text search should not be narrowed by the catalogue month window —
  // hide the date picker and drop `from`/`to` from the API calls.
  const searchActive = searchQuery.trim().length > 0;
  const effectiveMonthRange = searchActive ? null : monthRange;
  // Admin selection state
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [actionLoading, setActionLoading] = useState<boolean>(false);
  const [modifiedView, setModifiedView] = useState<ModifiedEdmView>('hidden');
  const cacheKey = edmTabCacheKey(category, searchQuery, filters, effectiveMonthRange, modifiedView);
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0);
  const fetchTab: EdmTabId =
    category === 'hidden' && modifiedView === 'ec' ? 'tagged-ec' : category;
  const isModifiedTab = category === 'hidden';
  const isModifiedEcView = isModifiedTab && modifiedView === 'ec';

  // Project filters hide the Upcoming Projects tab; search alone keeps it.
  const hasProjectFilters =
    (filters.regions?.length ?? 0) > 0 ||
    (filters.districts?.length ?? 0) > 0 ||
    (filters.propertyTypes?.length ?? 0) > 0 ||
    (filters.projectIds?.length ?? 0) > 0;

  const categories = useMemo<{ id: EdmCategoryId; label: string }[]>(() => {
    const base = EDM_CATEGORIES.filter(
      (c) => !(c.id === 'upcoming' && hasProjectFilters),
    ).map((c) => ({ id: c.id as EdmCategoryId, label: c.label }));
    if (isAdmin) {
      return [...base, { id: 'hidden' as EdmCategoryId, label: 'EDM Settings' }];
    }
    return base;
  }, [isAdmin, hasProjectFilters]);

  // If Upcoming was selected when filters land, fall back to All.
  useEffect(() => {
    if (hasProjectFilters && category === 'upcoming') {
      setCategory('all');
    }
  }, [hasProjectFilters, category]);

  // Reset selection when switching tabs or the EDM Settings view.
  useEffect(() => {
    setSelectedIds([]);
  }, [category, modifiedView]);

  const handleToggleSelect = (id: string, checked: boolean) => {
    setSelectedIds((prev) => {
      if (checked) {
        return [...prev, id];
      } else {
        return prev.filter((x) => x !== id);
      }
    });
  };

  const handleToggleSelectAll = () => {
    const allItemIds = items.map((item) => item.id);
    const allSelected = allItemIds.every((id) => selectedIds.includes(id));
    if (allSelected) {
      setSelectedIds((prev) => prev.filter((id) => !allItemIds.includes(id)));
    } else {
      setSelectedIds((prev) => {
        const next = [...prev];
        for (const id of allItemIds) {
          if (!next.includes(id)) {
            next.push(id);
          }
        }
        return next;
      });
    }
  };

  const handleHideSelected = async () => {
    if (selectedIds.length === 0) return;
    const ok = window.confirm(`Are you sure you want to hide the ${selectedIds.length} selected EDM(s)?`);
    if (!ok) return;

    setActionLoading(true);
    try {
      await projectEssentialApi.hideEdms(selectedIds);
      setSelectedIds([]);
      // Wipe cache and trigger reload
      setTabData({});
      setServerTabCounts({});
      setRefreshTrigger((prev) => prev + 1);
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Failed to hide EDMs');
    } finally {
      setActionLoading(false);
    }
  };

  const handleTagSelectedAsEc = async () => {
    if (selectedIds.length === 0) return;
    const ok = window.confirm(
      `Tag the ${selectedIds.length} selected EDM(s) as Executive Condo? They will show when the Executive Condo filter is applied.`,
    );
    if (!ok) return;

    setActionLoading(true);
    try {
      await projectEssentialApi.tagEdmsAsEc(selectedIds);
      setSelectedIds([]);
      setTabData({});
      setServerTabCounts({});
      setRefreshTrigger((prev) => prev + 1);
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Failed to tag EDMs as EC');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnhideSelected = async () => {
    if (selectedIds.length === 0) return;
    const ok = window.confirm(`Are you sure you want to unhide the ${selectedIds.length} selected EDM(s)?`);
    if (!ok) return;

    setActionLoading(true);
    try {
      await projectEssentialApi.unhideEdms(selectedIds);
      setSelectedIds([]);
      // Wipe cache and trigger reload
      setTabData({});
      setServerTabCounts({});
      setRefreshTrigger((prev) => prev + 1);
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Failed to unhide EDMs');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUntagSelectedAsEc = async () => {
    if (selectedIds.length === 0) return;
    const ok = window.confirm(
      `Untag the ${selectedIds.length} selected EDM(s) as Executive Condo?`,
    );
    if (!ok) return;

    setActionLoading(true);
    try {
      await projectEssentialApi.untagEdmsAsEc(selectedIds);
      setSelectedIds([]);
      setTabData({});
      setServerTabCounts({});
      setRefreshTrigger((prev) => prev + 1);
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Failed to untag EDMs as EC');
    } finally {
      setActionLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    projectEssentialApi
      .getEdmTabCounts(searchQuery, filters, effectiveMonthRange ?? undefined)
      .then((res) => {
        if (!cancelled) setServerTabCounts(res.counts);
      })
      .catch(() => {
        // Count badges are supplementary — a failure here shouldn't
        // block the catalogue from loading.
      });
    return () => {
      cancelled = true;
    };
  }, [searchQuery, filters, effectiveMonthRange, refreshTrigger]);

  useEffect(() => {
    let cancelled = false;
    // Only skip when this cache key is already populated. Do not gate on
    // `loadingTabs` — a cancelled in-flight request used to leave loading
    // true, so the remounted effect bailed forever (stuck spinner).
    if (tabData[cacheKey]) return;

    setLoadingTabs((prev) => ({ ...prev, [cacheKey]: true }));
    setError(null);

    projectEssentialApi
      .getEdmsByTab(
        fetchTab,
        undefined,
        searchQuery,
        filters,
        effectiveMonthRange ?? undefined,
      )
      .then((res) => {
        if (cancelled) return;
        setTabData((prev) => ({ ...prev, [cacheKey]: res.items.map(mapTabItem) }));
      })
      .catch((err) => {
        if (cancelled) return;
        setError((err as Error)?.message || 'Failed to load');
      })
      .finally(() => {
        if (cancelled) return;
        setLoadingTabs((prev) => ({ ...prev, [cacheKey]: false }));
      });

    return () => {
      cancelled = true;
      setLoadingTabs((prev) => ({ ...prev, [cacheKey]: false }));
    };
    // We intentionally exclude tabData/loadingTabs from deps — they
    // mutate as a side-effect of this effect and would re-fire it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchTab, searchQuery, filters, effectiveMonthRange, cacheKey, refreshTrigger]);

  const items = tabData[cacheKey] ?? [];
  const isLoading = Boolean(loadingTabs[cacheKey]) && !tabData[cacheKey];

  // Per-tab count badges — always sourced from the server so they
  // reflect the active search (EDM name + linked project name) even
  // before each chip is opened.
  const tabCounts = useMemo<Record<string, number | null>>(() => {
    const acc: Record<string, number | null> = {};
    for (const cat of categories) {
      if (cat.id === 'hidden') {
        acc[cat.id] =
          (modifiedView === 'ec' ? serverTabCounts['tagged-ec'] : serverTabCounts.hidden) ?? null;
      } else {
        acc[cat.id] = serverTabCounts[cat.id] ?? null;
      }
    }
    return acc;
  }, [categories, serverTabCounts, modifiedView]);

  return (
    <Box>
      {/* Header Row: All EDM Templates title + Month Range Picker */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'flex-start' },
          justifyContent: 'space-between',
          gap: 1.5,
          mb: { xs: 1.5, sm: 2 },
        }}
      >
        <Stack spacing={0.5}>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 700,
              color: 'var(--color-text-strong)',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              fontSize: { xs: '0.95rem', sm: '1.05rem' },
            }}
          >
            {searchActive ? 'EDM Search Results' : 'All EDM Templates'}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'var(--color-text-muted)',
              fontSize: { xs: '0.85rem', sm: '0.9rem' },
            }}
          >
            Email-marketing posters and VVIP passes you can download or re-share.
          </Typography>
          {!searchActive && (
            <Typography
              variant="caption"
              sx={{
                color: 'error.main',
                fontSize: '0.85rem',
                fontWeight: '900 !important',
                display: 'block',
                mt: 0.25,
              }}
            >
              Showing EDMs from {formatMonthRange(monthRange)}
            </Typography>
          )}
        </Stack>
        {!searchActive && (
          <Box sx={{ flexShrink: 0, width: { xs: '100%', sm: 'auto' } }}>
            <MonthRangePicker
              value={monthRange}
              onChange={onMonthRangeChange}
            />
          </Box>
        )}
      </Box>

      {/* Category tabs — full width with bottom border */}
      <Tabs
        value={category}
        onChange={(_e, v: EdmCategoryId) => setCategory(v)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{
          minHeight: 40,
          mb: { xs: 1.5, sm: 2 },
          borderBottom: '1px solid var(--color-border-strong)',
          '& .MuiTab-root': {
            textTransform: 'none',
            fontWeight: 700,
            color: 'var(--color-text-muted)',
            minHeight: 40,
            px: { xs: 1.25, sm: 2 },
          },
          '& .Mui-selected': { color: 'var(--color-accent) !important' },
          '& .MuiTabs-indicator': { backgroundColor: 'var(--color-accent)' },
        }}
      >
        {categories.map((cat) => {
          const count = tabCounts[cat.id];
          const isActive = category === cat.id;
          const isHiddenTab = cat.id === 'hidden';
          return (
            <Tab
              key={cat.id}
              value={cat.id}
              sx={{
                ...(isHiddenTab && !isActive && {
                  color: 'error.main',
                  opacity: 0.85,
                  '&:hover': { opacity: 1 },
                }),
              }}
              label={
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                  {isHiddenTab && (
                    <SettingsIcon
                      sx={{
                        fontSize: '1.1rem',
                        color: isActive ? 'inherit' : 'error.main',
                      }}
                    />
                  )}
                  <span>{cat.label}</span>
                  {count != null && (
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: 22,
                        height: 20,
                        px: 0.75,
                        borderRadius: 999,
                        fontSize: '0.72rem',
                        fontWeight: 700,
                        bgcolor: isActive
                          ? (isHiddenTab ? 'error.main' : 'var(--color-accent)')
                          : (isHiddenTab ? 'error.light' : 'var(--color-surface-elevated)'),
                        color: isActive
                          ? '#fff'
                          : (isHiddenTab ? 'error.contrastText' : 'var(--color-text-muted)'),
                        border: isActive
                          ? `1px solid ${isHiddenTab ? '#d32f2f' : 'var(--color-accent)'}`
                          : `1px solid ${isHiddenTab ? '#ef5350' : 'var(--color-border-strong)'}`,
                        transition:
                          'background-color 120ms ease, color 120ms ease, border-color 120ms ease',
                      }}
                    >
                      {count}
                    </Box>
                  )}
                </Box>
              }
            />
          );
        })}
      </Tabs>

      {/* Admin actions: EDM Settings view switcher (always) plus
          selection tools when the current grid has items. */}
      {isAdmin && (isModifiedTab || items.length > 0) && (
        <Box
          sx={{
            mb: 2.5,
            p: 1.5,
            borderRadius: 2,
            bgcolor: 'var(--color-surface-elevated)',
            border: '1.5px solid var(--color-border-strong)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1.5,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap">
            {isModifiedTab && (
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mr: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={modifiedView === 'hidden'}
                      onChange={() => setModifiedView('hidden')}
                      size="small"
                      sx={{
                        color: 'var(--color-accent)',
                        '&.Mui-checked': { color: 'var(--color-accent)' },
                      }}
                    />
                  }
                  label="Hidden"
                  sx={{
                    mr: 1,
                    '& .MuiFormControlLabel-label': {
                      fontWeight: 700,
                      fontSize: '0.875rem',
                      color: 'var(--color-text-strong)',
                    },
                  }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={modifiedView === 'ec'}
                      onChange={() => setModifiedView('ec')}
                      size="small"
                      sx={{
                        color: 'var(--color-accent)',
                        '&.Mui-checked': { color: 'var(--color-accent)' },
                      }}
                    />
                  }
                  label="EC"
                  sx={{
                    mr: 0,
                    '& .MuiFormControlLabel-label': {
                      fontWeight: 700,
                      fontSize: '0.875rem',
                      color: 'var(--color-text-strong)',
                    },
                  }}
                />
              </Stack>
            )}
            {items.length > 0 && (
              <>
                <Typography variant="body2" sx={{ fontWeight: 700, color: 'var(--color-text-strong)' }}>
                  {selectedIds.length} out of {items.length} selected
                </Typography>
                <Button
                  size="small"
                  onClick={handleToggleSelectAll}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    color: 'var(--color-accent)',
                  }}
                >
                  {items.every((item) => selectedIds.includes(item.id)) ? 'Deselect All' : 'Select All'}
                </Button>
              </>
            )}
          </Stack>

          {selectedIds.length > 0 && (
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setSelectedIds([])}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 999,
                  borderColor: 'var(--color-border-strong)',
                  color: 'var(--color-text-strong)',
                  bgcolor: 'var(--color-surface)',
                  '&:hover': {
                    borderColor: 'var(--color-border-strongest)',
                    bgcolor: 'var(--color-surface-hover)',
                  },
                }}
              >
                Clear Selection
              </Button>
              {isModifiedTab ? (
                isModifiedEcView ? (
                  <Button
                    size="small"
                    variant="contained"
                    onClick={handleUntagSelectedAsEc}
                    disabled={actionLoading}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 700,
                      borderRadius: 999,
                      bgcolor: 'var(--color-accent)',
                      color: '#fff',
                      '&:hover': { bgcolor: 'var(--color-accent-strong)' },
                    }}
                  >
                    {actionLoading ? <CircularProgress size={16} color="inherit" /> : 'Untag as EC'}
                  </Button>
                ) : (
                  <Button
                    size="small"
                    variant="contained"
                    onClick={handleUnhideSelected}
                    disabled={actionLoading}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 700,
                      borderRadius: 999,
                      bgcolor: 'var(--color-accent)',
                      color: '#fff',
                      '&:hover': { bgcolor: 'var(--color-accent-strong)' },
                    }}
                  >
                    {actionLoading ? <CircularProgress size={16} color="inherit" /> : 'Unhide Selected'}
                  </Button>
                )
              ) : (
                <>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleTagSelectedAsEc}
                    disabled={actionLoading}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 700,
                      borderRadius: 999,
                      borderColor: 'var(--color-accent)',
                      color: 'var(--color-accent-strong)',
                      bgcolor: 'var(--color-surface)',
                      '&:hover': {
                        borderColor: 'var(--color-accent-strong)',
                        bgcolor: 'var(--color-accent-soft)',
                      },
                    }}
                  >
                    {actionLoading ? <CircularProgress size={16} color="inherit" /> : 'Tag as EC'}
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={handleHideSelected}
                    disabled={actionLoading}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 700,
                      borderRadius: 999,
                      bgcolor: 'error.main',
                      color: '#fff',
                      '&:hover': { bgcolor: 'error.dark' },
                    }}
                  >
                    {actionLoading ? <CircularProgress size={16} color="inherit" /> : 'Hide Selected'}
                  </Button>
                </>
              )}
            </Stack>
          )}
        </Box>
      )}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : error && items.length === 0 ? (
        <Typography variant="body2" color="error" sx={{ py: 4, textAlign: 'center' }}>
          {error}
        </Typography>
      ) : (
        <PaginatedPosterGrid
          items={items}
          emptyLabel={
            isModifiedTab
              ? isModifiedEcView
                ? 'No EDMs are tagged as Executive Condo.'
                : 'No hidden EDMs.'
              : searchActive
                ? 'No EDMs match your search or filter.'
                : `No EDMs match your search or filter for ${formatMonthRange(monthRange)}.`
          }
          // Reset pagination whenever the filter narrows (tab change
          // or search update) so the user always lands on page 1.
          resetKey={cacheKey}
          onPreview={(item) => logEdmPreviewed(item.id)}
          onDownloadClick={(item) => logEdmDownloaded(item.id)}
          renderItem={(item) => (
            <Box key={item.id} sx={{ position: 'relative', width: '100%', zIndex: 1 }}>
              {isAdmin && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 10,
                    left: 10,
                    zIndex: 2,
                    bgcolor: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: 1,
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 30,
                    height: 30,
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Checkbox
                    checked={selectedIds.includes(item.id)}
                    onChange={(e) => handleToggleSelect(item.id, e.target.checked)}
                    size="small"
                    sx={{
                      p: 0.25,
                      color: 'var(--color-accent)',
                      '&.Mui-checked': {
                        color: 'var(--color-accent)',
                      },
                    }}
                  />
                </Box>
              )}
              <PosterCard
                item={item}
                onPreview={(i) => logEdmPreviewed(i.id)}
                onDownloadClick={(i) => logEdmDownloaded(i.id)}
                shareKind="edm"
              />
            </Box>
          )}
        />
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function ProjectEdmPage() {
  const navigate = useNavigate();
  const [filters, setFilters] = usePeFilters();
  const { search, setSearch, dismissDropdownKey, dropdownContent } = usePeSearchDropdown(filters);
  const [filtersOpen, setFiltersOpen] = useState(false);
  // Date-range context for the dialog's month picker. Default = last
  // 6 months. Not wired to the catalogue API yet — ready to plug in
  // when the endpoint supports a `from/to` filter.
  const [monthRange, setMonthRange] = useState<MonthRange>(() => lastNMonthsRange(6));

  const [isAdmin, setIsAdmin] = useState(false);
  useEffect(() => {
    projectEssentialApi
      .getMe()
      .then((user) => {
        const role = user?.role?.toUpperCase();
        setIsAdmin(role === 'ADMIN');
      })
      .catch(() => {
        setIsAdmin(false);
      });
  }, []);

  const filterCount = countPeFilters(filters);

  const { chips: appliedFilterChips, clearAll: clearAllFilters } = useAppliedFilterChips(
    filters,
    setFilters,
  );

  const hideLatestRail = filterCount > 0;

  // ── Uploaded Recently — top 10 EDMs by CreatedAt DESC. ──
  const [recentItems, setRecentItems] = useState<PosterItem[]>([]);
  const [recentLoading, setRecentLoading] = useState(true);
  const [recentError, setRecentError] = useState<string | null>(null);

  useEffect(() => {
    if (hideLatestRail) {
      setRecentItems([]);
      setRecentLoading(false);
      setRecentError(null);
      return;
    }
    let mounted = true;
    setRecentLoading(true);
    setRecentError(null);
    // Month range stays on the "All EDM Templates" section only.
    projectEssentialApi
      .getEdmsByTab('recent', MAX_UPLOADED_RECENTLY)
      .then((res) => {
        if (mounted) setRecentItems(res.items.map(mapTabItem));
      })
      .catch((err) => {
        if (mounted) setRecentError((err as Error)?.message || 'Failed to load recent EDMs');
      })
      .finally(() => {
        if (mounted) setRecentLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [hideLatestRail]);

  // Alias avoids a TSX parse quirk where `recentItems` inside JSX
  // ternaries can be tokenised as `recent` + `Items`.
  const uploadedRecently = recentItems;

  return (
    <Stack spacing={{ xs: 2, sm: 2.5 }} sx={{ minWidth: 0 }}>
      <Box>
        <Button
          onClick={() => navigate('/project-essential')}
          startIcon={<ArrowBackIcon />}
          variant="outlined"
          size="small"
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 999,
            borderColor: 'var(--color-border-strong)',
            color: 'var(--color-text-strong)',
            bgcolor: 'var(--color-surface)',
            '&:hover': {
              borderColor: 'var(--color-accent)',
              bgcolor: 'var(--color-accent-soft)',
            },
          }}
        >
          Back to Dashboard
        </Button>
      </Box>

      <Typography
        variant="h4"
        sx={{
          fontWeight: 800,
          color: 'var(--color-accent)',
          textAlign: 'center',
          letterSpacing: '0.01em',
          fontSize: { xs: '1.6rem', sm: '2rem', md: '2.25rem' },
        }}
      >
        Project EDM
      </Typography>

      <PageSearchBar
        value={search}
        onChange={setSearch}
        placeholder="Search projects, EDMs, eBooks and reports…"
        onSubmit={(query) => navigate(buildPeSearchPageHref(query, filters))}
        filterCount={filterCount}
        onOpenFilters={() => setFiltersOpen(true)}
        appliedFilters={appliedFilterChips}
        onClearAllFilters={clearAllFilters}
        dropdownContent={dropdownContent}
        dismissDropdownKey={dismissDropdownKey}
      />

      <ProjectNavButtons active="edm" />

      {/* Latest rail is hidden while project filters are active so the
          catalogue below is the narrowed browse surface. */}
      {!hideLatestRail && (
      <Box>
        <SectionHeader title="Latest EDMs" />
        <Box
          sx={{
            py: 1.5,
            px: { xs: 0.5, sm: 1 },
          }}
        >
          {recentLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : recentError && uploadedRecently.length === 0 ? (
            <Typography
              variant="body2"
              color="error"
              sx={{ px: 1, py: 1 }}
            >
              {recentError}
            </Typography>
          ) : uploadedRecently.length === 0 ? (
            <Typography
              variant="body2"
              sx={{ color: 'var(--color-text-muted)', px: 1, py: 1 }}
            >
              No recent EDMs.
            </Typography>
          ) : (
            <DotCarousel
              slides={uploadedRecently.map((item) => ({
                key: item.id,
                node: (
                  <PosterCard
                    item={item}
                    onPreview={(i) => logEdmPreviewed(i.id)}
                    onDownloadClick={(i) => logEdmDownloaded(i.id)}
                    shareKind="edm"
                  />
                ),
              }))}
              slideFlex={EDM_SLIDE_FLEX}
              gap={{ xs: 0.5, sm: 1.25 }}
            />
          )}
        </Box>
      </Box>
      )}

      {/* ─── Catalogue: tab-filtered, paginated grid ─── */}
      <EdmCatalogue
        searchQuery=""
        filters={filters}
        monthRange={monthRange}
        onMonthRangeChange={setMonthRange}
        isAdmin={isAdmin}
      />

      <ProjectFiltersDialog
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        value={filters}
        onApply={(next) => {
          navigate(buildPeSearchPageHref(search.trim(), next));
        }}
      />

      <SearchPendingPreviewHost />
    </Stack>
  );
}
